import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { components } from '@trackercop/contracts';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { CONSENT_MODES } from '@/lib/constants';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { RecipientsTable } from '@/components/data-flow/recipients-table';
import { DataFlowSankey } from '@/components/data-flow/data-flow-sankey';
import { VendorCard } from '@/components/data-flow/vendor-card';
import {
  buildSiteDataFlowGraph,
  type RecipientTableRow,
  type SiteDataFlowSankeyNode,
} from '@/lib/site-data-flow-graph';
import { formatDateTime } from '@/lib/format';

type ConsentMode = components['schemas']['ConsentMode'];
type DataFlowVendor = components['schemas']['DataFlowVendor'];

interface DataFlowSearch {
  consentMode?: ConsentMode;
  pageId?: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const siteDataFlowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sites/$siteId/data-flows',
  component: SiteDataFlowsPage,
  validateSearch: (search: Record<string, unknown>): DataFlowSearch => {
    const out: DataFlowSearch = {};
    const mode = search.consentMode;
    if (mode === 'no-consent' || mode === 'accept-all' || mode === 'reject-all') {
      out.consentMode = mode;
    }
    const pageId = search.pageId;
    if (typeof pageId === 'string' && UUID_RE.test(pageId)) {
      out.pageId = pageId;
    }
    return out;
  },
});

function SiteDataFlowsPage() {
  const { siteId } = siteDataFlowsRoute.useParams();
  const search = siteDataFlowsRoute.useSearch();
  const navigate = siteDataFlowsRoute.useNavigate();
  const consentMode = search.consentMode;
  const pageId = search.pageId;

  const [selectedRow, setSelectedRow] = useState<RecipientTableRow | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<DataFlowVendor | null>(null);

  const summaryQuery = useQuery({
    queryKey: queryKeys.dataFlows.bySite(siteId, { consentMode, pageId }),
    retry: false,
    queryFn: async () => {
      const query: { consentMode?: ConsentMode; pageId?: string } = {};
      if (consentMode) query.consentMode = consentMode;
      if (pageId) query.pageId = pageId;
      const { data, error } = await apiClient.GET('/sites/{siteId}/data-flows', {
        params: {
          path: { siteId },
          query,
        },
      });
      if (error) throw error;
      return data;
    },
  });

  const graph = useMemo(
    () => (summaryQuery.data ? buildSiteDataFlowGraph(summaryQuery.data) : null),
    [summaryQuery.data],
  );

  function setConsentFilter(next: ConsentMode | undefined) {
    navigate({
      // TanStack Router strips undefined keys from search params, so
      // explicit `undefined` is how we clear the filter — no manual
      // string juggling. We intentionally preserve `pageId` here so
      // toggling consent inside a per-page view doesn't drop the page
      // selection.
      search: () => {
        const out: DataFlowSearch = {};
        if (next) out.consentMode = next;
        if (pageId) out.pageId = pageId;
        return out;
      },
      replace: true,
    });
  }

  function setPageFilter(next: string | undefined) {
    navigate({
      search: () => {
        const out: DataFlowSearch = {};
        if (consentMode) out.consentMode = consentMode;
        if (next) out.pageId = next;
        return out;
      },
      replace: true,
    });
  }

  function exportCsv() {
    const params = new URLSearchParams();
    if (consentMode) params.set('consentMode', consentMode);
    if (pageId) params.set('pageId', pageId);
    const qs = params.toString();
    const url = `/api/sites/${encodeURIComponent(siteId)}/data-flows.csv${qs ? `?${qs}` : ''}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  if (summaryQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading data flows…</p>;
  }
  if (summaryQuery.isError || !summaryQuery.data || !graph) {
    return (
      <div
        className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900"
        data-testid="data-flow-error"
      >
        Data flows unavailable. The site may have no completed scans.
      </div>
    );
  }

  const summary = summaryQuery.data;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Data-flow recipients</h1>
          <p className="text-xs text-muted-foreground">
            Latest scan crawled {formatDateTime(summary.crawledAt)} ·{' '}
            {summary.pageCount} {summary.pageCount === 1 ? 'page' : 'pages'} ·{' '}
            {graph.tableRows.length} recipients
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            className="h-8 w-44"
            value={consentMode ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setConsentFilter(v === '' ? undefined : (v as ConsentMode));
            }}
            data-testid="data-flow-consent-filter"
          >
            <option value="">All consent modes</option>
            {CONSENT_MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            data-testid="data-flow-export-csv"
          >
            Export CSV
          </Button>
        </div>
      </div>

      {pageId && (
        <div
          className="flex items-center justify-between rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900"
          data-testid="data-flow-page-banner"
        >
          <span>
            Filtered to page <span className="font-mono">{pageId.slice(0, 8)}…</span> —
            showing only flows that originated on this page.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageFilter(undefined)}
            data-testid="data-flow-clear-page"
          >
            Show all pages
          </Button>
        </div>
      )}

      {graph.collapsedEdgeCount > 0 && (
        <div
          className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="data-flow-collapsed-banner"
        >
          {graph.collapsedEdgeCount} low-weight chains collapsed for legibility.
        </div>
      )}

      <RecipientsTable
        rows={graph.tableRows}
        onSelectRecipient={(row) => {
          setSelectedRow(row);
          setSelectedVendor(row.vendor);
        }}
      />

      <DataFlowSankey
        graph={graph}
        onSelectNode={(node: SiteDataFlowSankeyNode) => {
          if (node.kind === 'vendor' || node.kind === 'recipient') {
            const v = node.vendor;
            if (v) {
              const matchedRow =
                graph.tableRows.find((r) => r.vendor.id === v.id) ?? null;
              setSelectedVendor(v);
              setSelectedRow(matchedRow);
            }
          }
        }}
      />

      {selectedVendor && (
        <VendorCard
          open
          vendor={selectedVendor}
          recipientRow={selectedRow}
          crawlId={summary.latestCrawlId}
          activePageId={pageId ?? null}
          availablePages={summary.pages}
          onSelectPage={(next) => setPageFilter(next ?? undefined)}
          onClose={() => {
            setSelectedVendor(null);
            setSelectedRow(null);
          }}
        />
      )}
    </div>
  );
}
